import { useState } from "react";
import { tiposVistoria, tecnicos } from "@/mocks/vistorias";
import { estadosFitossanitarios, classesRisco, recomendacoesManejo } from "@/mocks/cta";

interface NewInspectionModalProps {
  onClose: () => void;
}

const steps = [
  { label: "Árvore", icon: "ri-search-line" },
  { label: "Localização", icon: "ri-map-pin-line" },
  { label: "Medidas", icon: "ri-ruler-line" },
  { label: "Fitossanidade", icon: "ri-heart-pulse-line" },
  { label: "Risco", icon: "ri-alert-line" },
  { label: "Recomendação", icon: "ri-clipboard-line" },
  { label: "Fotos", icon: "ri-camera-line" },
  { label: "Revisar", icon: "ri-check-line" },
];

export default function NewInspectionModal({ onClose }: NewInspectionModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState({
    codigoCta: "",
    tipo: "Vistoria Técnica",
    tecnico: "",
    data: new Date().toISOString().split("T")[0],
    dap: "",
    altura: "",
    diametroCopa: "",
    estadoFitossanitario: "bom",
    classeRisco: "baixo",
    recomendacao: "Sem intervenção",
    parecer: "",
    fotos: 0,
  });

  const update = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const inputClasses = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors";
  const selectClasses = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";
  const labelClasses = "text-xs font-medium text-foreground-500 mb-1 block";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-foreground-900/40" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl border border-background-200/70 shadow-xl w-full max-w-[640px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="shrink-0 px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-foreground-900 font-heading">Nova Vistoria</h2>
            <p className="text-xs text-foreground-400">Etapa {currentStep + 1} de {steps.length}: {steps[currentStep].label}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Steps indicator */}
        <div className="shrink-0 px-5 py-3 border-b border-background-100 flex items-center gap-1.5 overflow-x-auto">
          {steps.map((step, idx) => (
            <div key={step.label} className="flex items-center gap-1.5 shrink-0">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  idx < currentStep
                    ? "bg-primary-500 text-white"
                    : idx === currentStep
                    ? "bg-primary-100 text-primary-700 border-2 border-primary-300"
                    : "bg-background-100 text-foreground-400"
                }`}
              >
                {idx < currentStep ? <i className="ri-check-line"></i> : idx + 1}
              </div>
              <span className={`text-xs whitespace-nowrap ${idx === currentStep ? "text-foreground-800 font-medium" : "text-foreground-400"}`}>
                {step.label}
              </span>
              {idx < steps.length - 1 && <div className="w-4 h-px bg-background-200"></div>}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Step 1: Select tree */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div>
                <label className={labelClasses}>Código CTA</label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    placeholder="Ex: CTA-GO-5208707-000001"
                    value={form.codigoCta}
                    onChange={(e) => update("codigoCta", e.target.value)}
                    className={`${inputClasses} pl-9`}
                  />
                </div>
                <p className="text-xs text-foreground-400 mt-1">Digite o código CTA ou busque por espécie/logradouro</p>
              </div>
              <div className="bg-background-50 rounded-xl p-4 border border-background-200/50">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Resultado da Busca</h4>
                {form.codigoCta.length > 3 ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className="ri-plant-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="font-mono text-xs text-accent-600 font-medium">CTA-GO-5208707-000001</p>
                      <p className="text-sm font-semibold text-foreground-800">Ipê-amarelo</p>
                      <p className="text-xs text-foreground-400">Handroanthus chrysotrichus — Av. Goiás, 1530, Setor Central</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400">Busque por um código CTA válido para selecionar a árvore.</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Confirm location */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="bg-accent-50 rounded-xl p-4 border border-accent-200/40">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-map-pin-line text-accent-500"></i>
                  <span className="text-sm font-semibold text-accent-800">Localização Confirmada</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-xs text-foreground-400">Município:</span> <span className="font-medium text-foreground-700">Goiânia/GO</span></div>
                  <div><span className="text-xs text-foreground-400">Bairro:</span> <span className="font-medium text-foreground-700">Setor Central</span></div>
                  <div className="col-span-2"><span className="text-xs text-foreground-400">Logradouro:</span> <span className="font-medium text-foreground-700">Av. Goiás, 1530</span></div>
                  <div className="col-span-2"><span className="text-xs text-foreground-400">Coordenadas:</span> <span className="font-medium text-foreground-700 font-mono">-16.6789, -49.2645</span></div>
                </div>
              </div>
              <div className="bg-background-200/50 rounded-xl h-40 flex items-center justify-center">
                <div className="text-center">
                  <i className="ri-map-2-line text-foreground-400 text-2xl mb-1 block"></i>
                  <span className="text-xs text-foreground-400">Mini mapa (Google Maps embed)</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Measurements */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelClasses}>DAP (cm)</label>
                  <input type="number" placeholder="45" value={form.dap} onChange={(e) => update("dap", e.target.value)} className={inputClasses} />
                </div>
                <div>
                  <label className={labelClasses}>Altura (m)</label>
                  <input type="number" placeholder="12.5" value={form.altura} onChange={(e) => update("altura", e.target.value)} className={inputClasses} step="0.1" />
                </div>
                <div>
                  <label className={labelClasses}>Diâmetro Copa (m)</label>
                  <input type="number" placeholder="8.2" value={form.diametroCopa} onChange={(e) => update("diametroCopa", e.target.value)} className={inputClasses} step="0.1" />
                </div>
              </div>
              <div>
                <label className={labelClasses}>Tipo de Vistoria</label>
                <select value={form.tipo} onChange={(e) => update("tipo", e.target.value)} className={selectClasses}>
                  {tiposVistoria.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClasses}>Técnico Responsável</label>
                <select value={form.tecnico} onChange={(e) => update("tecnico", e.target.value)} className={selectClasses}>
                  <option value="">Selecione...</option>
                  {tecnicos.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClasses}>Data da Vistoria</label>
                <input type="date" value={form.data} onChange={(e) => update("data", e.target.value)} className={inputClasses} />
              </div>
            </div>
          )}

          {/* Step 4: Fitossanidade */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <label className={labelClasses}>Estado Fitossanitário</label>
                <div className="grid grid-cols-1 gap-2">
                  {estadosFitossanitarios.map((e) => (
                    <label
                      key={e}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        form.estadoFitossanitario === e
                          ? "border-primary-300 bg-primary-50"
                          : "border-background-200/70 hover:bg-background-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="estado"
                        value={e}
                        checked={form.estadoFitossanitario === e}
                        onChange={(k) => update("estadoFitossanitario", k.target.value)}
                        className="accent-primary-500"
                      />
                      <span className="text-sm font-medium text-foreground-700 capitalize">{e}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Risk */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <label className={labelClasses}>Classe de Risco</label>
                <div className="grid grid-cols-1 gap-2">
                  {classesRisco.map((r) => (
                    <label
                      key={r}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        form.classeRisco === r
                          ? r === "critico" ? "border-danger-300 bg-danger-50" : r === "alto" ? "border-warning-300 bg-warning-50" : r === "medio" ? "border-caution-300 bg-caution-50" : "border-primary-300 bg-primary-50"
                          : "border-background-200/70 hover:bg-background-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="risco"
                        value={r}
                        checked={form.classeRisco === r}
                        onChange={(k) => update("classeRisco", k.target.value)}
                        className="accent-primary-500"
                      />
                      <span className="text-sm font-medium text-foreground-700 capitalize">{r}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Recommendation */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <div>
                <label className={labelClasses}>Recomendação de Manejo</label>
                <select value={form.recomendacao} onChange={(e) => update("recomendacao", e.target.value)} className={selectClasses}>
                  {recomendacoesManejo.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClasses}>Parecer Técnico</label>
                <textarea
                  rows={5}
                  placeholder="Descreva as observações técnicas, justificativas e recomendações detalhadas..."
                  value={form.parecer}
                  onChange={(e) => update("parecer", e.target.value)}
                  maxLength={500}
                  className={`${inputClasses} resize-none`}
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">{form.parecer.length}/500 caracteres</p>
              </div>
            </div>
          )}

          {/* Step 7: Photos */}
          {currentStep === 6 && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-background-200 rounded-xl p-8 text-center cursor-pointer hover:border-primary-300 hover:bg-primary-50/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-camera-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-sm font-medium text-foreground-700 mb-1">Adicionar Fotos</p>
                <p className="text-xs text-foreground-400">Arraste imagens ou clique para selecionar. Formatos: JPG, PNG, HEIC</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="aspect-square rounded-lg bg-background-100 border border-background-200/60 flex items-center justify-center">
                    <i className="ri-image-add-line text-foreground-300 text-lg"></i>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 8: Review */}
          {currentStep === 7 && (
            <div className="space-y-4">
              <div className="bg-background-50 rounded-xl p-4 border border-background-200/50 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Código CTA:</span>
                  <span className="font-mono text-accent-600 font-medium">{form.codigoCta || "CTA-GO-5208707-000001"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Tipo:</span>
                  <span className="font-medium text-foreground-700">{form.tipo}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Técnico:</span>
                  <span className="font-medium text-foreground-700">{form.tecnico || "—"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Data:</span>
                  <span className="font-medium text-foreground-700">{new Date(form.data + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                </div>
                <hr className="border-background-200" />
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">DAP:</span>
                  <span className="font-medium text-foreground-700">{form.dap || "—"} cm</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Altura:</span>
                  <span className="font-medium text-foreground-700">{form.altura || "—"} m</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Copa:</span>
                  <span className="font-medium text-foreground-700">{form.diametroCopa || "—"} m</span>
                </div>
                <hr className="border-background-200" />
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Fitossanidade:</span>
                  <span className="font-medium text-foreground-700 capitalize">{form.estadoFitossanitario}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Risco:</span>
                  <span className="font-medium text-foreground-700 capitalize">{form.classeRisco}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-400">Recomendação:</span>
                  <span className="font-medium text-foreground-700">{form.recomendacao}</span>
                </div>
                {form.parecer && (
                  <>
                    <hr className="border-background-200" />
                    <div>
                      <span className="text-xs text-foreground-400 block mb-1">Parecer:</span>
                      <p className="text-sm text-foreground-700 leading-relaxed">{form.parecer}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-4 border-t border-background-200/70 flex items-center justify-between">
          <button
            onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
            disabled={currentStep === 0}
            className={`btn-outline text-xs ${currentStep === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            <i className="ri-arrow-left-line"></i>Anterior
          </button>
          {currentStep < steps.length - 1 ? (
            <button onClick={() => setCurrentStep(currentStep + 1)} className="btn-primary text-xs">
              Próximo<i className="ri-arrow-right-line"></i>
            </button>
          ) : (
            <button onClick={onClose} className="btn-primary text-xs">
              <i className="ri-check-line"></i>Salvar Vistoria
            </button>
          )}
        </div>
      </div>
    </div>
  );
}